import { useEffect, useState } from "react";
import {
  FilePdf,
  FileText as FileTextIcon,
  FileVideo,
  Image as ImageIcon,
  Paperclip,
  DownloadSimple,
} from "@phosphor-icons/react";
import { getAttachmentSignedUrls, getAttachmentImageSrc } from "@/lib/messaging";

/**
 * Renders the list of attachments for a single message. Resolves signed
 * URLs lazily on mount (1h TTL) and groups them into a responsive grid:
 *
 *   * image/* → thumbnail tile with click-to-open
 *   * video/mp4 | video/quicktime → inline <video controls>
 *   * everything else → file chip with icon + name + download link
 *
 * Keeping all three variants in one component so the message bubble
 * doesn't need to know attachment types.
 */
export default function MessageAttachments({ attachments, isMine }) {
  const [urls, setUrls] = useState({});       // storage_path → original signed URL (downloads/clicks)
  const [imgSrcs, setImgSrcs] = useState({}); // storage_path → small inline-image URL (renders)

  useEffect(() => {
    if (!attachments || attachments.length === 0) return;
    let alive = true;
    (async () => {
      const paths = attachments.map((a) => a.storage_path).filter(Boolean);
      const resolved = await getAttachmentSignedUrls(paths);
      if (!alive) return;
      setUrls(resolved);

      // For image attachments, also resolve a smaller transformed render.
      // Click-through / download still uses the original URL above.
      const imgPaths = attachments
        .filter((a) => (a.mime_type || "").startsWith("image/"))
        .map((a) => a.storage_path)
        .filter(Boolean);
      if (imgPaths.length > 0) {
        const imgEntries = await Promise.all(
          imgPaths.map(async (p) => [p, await getAttachmentImageSrc(p, { width: 800, quality: 75 })]),
        );
        if (!alive) return;
        const imgMap = {};
        for (const [p, u] of imgEntries) if (u) imgMap[p] = u;
        setImgSrcs(imgMap);
      }
    })();
    return () => { alive = false; };
  }, [attachments]);

  if (!attachments || attachments.length === 0) return null;

  const images = attachments.filter((a) => (a.mime_type || "").startsWith("image/"));
  const videos = attachments.filter((a) => (a.mime_type || "").startsWith("video/"));
  const files  = attachments.filter((a) =>
    !(a.mime_type || "").startsWith("image/") &&
    !(a.mime_type || "").startsWith("video/"),
  );

  return (
    <div
      data-testid="message-attachments"
      data-count={attachments.length}
      className="mt-1.5 flex flex-col gap-2"
    >
      {images.length > 0 && (
        <ul className={`grid gap-1.5 ${images.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
          {images.map((a) => (
            <li key={a.id}>
              <a
                href={urls[a.storage_path] || "#"}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="attachment-image-link"
                className="block"
              >
                {imgSrcs[a.storage_path] || urls[a.storage_path] ? (
                  <img
                    src={imgSrcs[a.storage_path] || urls[a.storage_path]}
                    alt={a.file_name}
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      // Transform unavailable — fall back to the original.
                      if (urls[a.storage_path] && e.currentTarget.src !== urls[a.storage_path]) {
                        e.currentTarget.src = urls[a.storage_path];
                      }
                    }}
                    className="h-auto w-full max-h-56 rounded-xl border border-neutral-200 object-cover"
                  />
                ) : (
                  <div className="flex h-28 w-full items-center justify-center rounded-xl border border-dashed border-neutral-300 bg-neutral-50 text-neutral-400">
                    <ImageIcon size={22} weight="duotone" />
                  </div>
                )}
              </a>
            </li>
          ))}
        </ul>
      )}

      {videos.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {videos.map((a) =>
            urls[a.storage_path] ? (
              <li key={a.id}>
                <video
                  controls
                  preload="metadata"
                  data-testid="attachment-video"
                  className="w-full rounded-xl border border-neutral-200"
                >
                  <source src={urls[a.storage_path]} type={a.mime_type || "video/mp4"} />
                </video>
              </li>
            ) : (
              <li key={a.id}>
                <AttachmentChip a={a} url={null} isMine={isMine} />
              </li>
            ),
          )}
        </ul>
      )}

      {files.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {files.map((a) => (
            <li key={a.id}>
              <AttachmentChip a={a} url={urls[a.storage_path]} isMine={isMine} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AttachmentChip({ a, url }) {
  const Icon =
    (a.mime_type || "").includes("pdf")  ? FilePdf :
    (a.mime_type || "").startsWith("video/") ? FileVideo :
    (a.mime_type || "").startsWith("text/")  ? FileTextIcon :
    Paperclip;

  return (
    <a
      href={url || "#"}
      target="_blank"
      rel="noreferrer noopener"
      data-testid="attachment-file-chip"
      data-mime={a.mime_type || ""}
      className="flex items-center gap-2 rounded-xl border border-neutral-200 bg-white px-3 py-2 text-xs text-neutral-700 transition hover:bg-neutral-50"
    >
      <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary-hover">
        <Icon size={14} weight="duotone" />
      </span>
      <span className="min-w-0 flex-1 truncate font-semibold">{a.file_name}</span>
      <span className="flex-shrink-0 text-[10px] text-neutral-400">
        {formatSize(a.byte_size)}
      </span>
      <DownloadSimple size={12} weight="bold" className="flex-shrink-0 text-neutral-400" />
    </a>
  );
}

function formatSize(bytes) {
  if (!bytes || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
